import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { attendanceAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatDate, formatTime, formatMinutesToHours, getStatusColor } from '../../lib/utils';
import { Calendar, Clock } from 'lucide-react';

const MyAttendance = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAttendance = useCallback(async () => {
    if (!user?.company_id || !user?.id) return;
    try {
      const response = await attendanceAPI.getAll(user.company_id, null, user.id);
      // Sort by date descending
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecords(sorted);
    } catch (err) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, user?.id]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Calculate summary stats
  const totalDays = records.length;
  const presentDays = records.filter(r => r.status === 'present').length;
  const lateDays = records.filter(r => r.status === 'late').length;
  const totalWorked = records.reduce((sum, r) => sum + (r.worked_minutes || 0), 0);
  const totalOT = records.reduce((sum, r) => sum + (r.overtime_minutes || 0), 0);

  if (loading) {
    return (
      <DashboardLayout title="My Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="My Attendance" 
      subtitle="View your attendance history"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold">{totalDays}</p>
            <p className="text-xs text-muted-foreground">Total Days</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-emerald-600">{presentDays}</p>
            <p className="text-xs text-muted-foreground">Present</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-amber-600">{lateDays}</p>
            <p className="text-xs text-muted-foreground">Late</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold">{Math.round(totalWorked / 60)}h</p>
            <p className="text-xs text-muted-foreground">Total Hours</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{Math.round(totalOT / 60)}h</p>
            <p className="text-xs text-muted-foreground">Overtime</p>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Attendance Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Worked</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>OT</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                  <TableCell className="font-medium">{formatDate(record.date)}</TableCell>
                  <TableCell>{record.clock_in ? formatTime(record.clock_in) : '-'}</TableCell>
                  <TableCell>{record.clock_out ? formatTime(record.clock_out) : '-'}</TableCell>
                  <TableCell>{formatMinutesToHours(record.worked_minutes)}</TableCell>
                  <TableCell>
                    {record.late_minutes > 0 ? (
                      <span className="text-amber-600">{record.late_minutes} mins</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {record.overtime_minutes > 0 ? (
                      <span className="text-purple-600">{formatMinutesToHours(record.overtime_minutes)}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(record.status)}>
                      {record.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {records.length === 0 && (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No attendance records yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default MyAttendance;
